import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import axios from 'axios'
import { env } from '@/infra/config/env'
import { getAuthToken } from '@/infra/auth'
import { ApiOperationError, isApiErrorResponse, mapHttpStatusToErrorType } from '@/infra'
import type { ApiResult } from './types'

export function getHttpClient(): AxiosInstance {
  sharedInstance ??= createHttpClient()
  return sharedInstance
}

export async function httpGet<T>(path: string): Promise<ApiResult<T>> {
  try {
    const response: AxiosResponse<T> = await getHttpClient().get(path)
    return { success: true, data: response.data }
  } catch (error) {
    return toErrorResult<T>(error)
  }
}

export async function httpPost<T, D = unknown>(path: string, data?: D): Promise<ApiResult<T>> {
  try {
    const response: AxiosResponse<T> = await getHttpClient().post(path, data)
    return { success: true, data: response.data }
  } catch (error) {
    return toErrorResult<T>(error)
  }
}

export async function httpPut<T, D = unknown>(path: string, data?: D): Promise<ApiResult<T>> {
  try {
    const response: AxiosResponse<T> = await getHttpClient().put(path, data)
    return { success: true, data: response.data }
  } catch (error) {
    return toErrorResult<T>(error)
  }
}

export async function httpPatch<T, D = unknown>(path: string, data?: D): Promise<ApiResult<T>> {
  try {
    const response: AxiosResponse<T> = await getHttpClient().patch(path, data)
    return { success: true, data: response.data }
  } catch (error) {
    return toErrorResult<T>(error)
  }
}

export async function httpDelete<T = void>(path: string): Promise<ApiResult<T>> {
  try {
    const response: AxiosResponse<T> = await getHttpClient().delete(path)
    return { success: true, data: response.data }
  } catch (error) {
    return toErrorResult<T>(error)
  }
}

function createHttpClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: env.BACKEND_BASE_URL,
    timeout: env.HTTP_CLIENT_TIMEOUT_MS,
    headers: { 'Content-Type': 'application/json' },
  })

  instance.interceptors.request.use(
    (config) => {
      const token = getAuthToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => Promise.reject(error instanceof Error ? error : new Error(String(error))),
  )

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status ?? 0
      const errorType = mapHttpStatusToErrorType(status)

      let message: string
      if (isApiErrorResponse(error.response?.data)) {
        message = error.response.data.message
      } else if (error.code === 'ECONNABORTED') {
        return Promise.reject(new ApiOperationError('Request timed out.', 'timeout'))
      } else if (error.message === 'Network Error') {
        return Promise.reject(new ApiOperationError('Network connection failed.', 'network_error'))
      } else {
        message = error.message || 'An unexpected error occurred.'
      }

      return Promise.reject(new ApiOperationError(message, errorType))
    },
  )

  return instance
}

function toErrorResult<T>(error: unknown): ApiResult<T> {
  if (error instanceof ApiOperationError) {
    return { success: false, error: error.errorType, message: error.message }
  }
  return { success: false, error: 'unknown_error', message: 'An unexpected error occurred.' }
}

let sharedInstance: AxiosInstance | null = null
